import React, { useState, useEffect } from 'react';
import { Container, Header, Tab, Button } from 'semantic-ui-react';
import styled from 'styled-components';
import { InComponentErrorRenderer } from './InComponentErrorRenderer';
const CodeBlock = styled.pre.attrs({
    className: 'dev-docs-code-block'
}) `
  background: #f8f8f8;
  border: 1px solid #e1e1e1;
  border-radius: 4px;
  padding: 1rem;
  overflow-x: auto;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.875rem;
  line-height: 1.4;
  margin: 1rem 0;
`;
const ExampleSection = styled.div.attrs({
    className: 'dev-docs-example-section'
}) `
  margin: 2rem 0;
  
  .example-title {
    color: #2185d0;
    margin-bottom: 1rem;
  }
`;
const ComponentDemo = styled.div.attrs({
    className: 'dev-docs-component-demo'
}) `
  border: 1px solid #e1e1e1;
  border-radius: 8px;
  padding: 1.5rem;
  background: white;
  margin: 1rem 0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;
const CodeContainer = styled.div.attrs({
    className: 'dev-docs-code-container'
}) `
  position: relative;
  
  .copy-button {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    z-index: 10;
  }
`;
const DevDocsPage = () => {
    const [formData, setFormData] = useState(null);
    const [lastEvent, setLastEvent] = useState('');
    const [eventCount, setEventCount] = useState(0);
    const [error, setError] = useState(null);
    const [copiedStates, setCopiedStates] = useState({});
    useEffect(() => {
        // Add event listener for form submission events
        const handleFormSubmit = (event) => {
            setFormData(event.detail);
            setLastEvent('Form Submitted');
            setEventCount(prev => prev + 1);
        };
        document.addEventListener('formsubmitted', handleFormSubmit);
        return () => {
            document.removeEventListener('formsubmitted', handleFormSubmit);
        };
    }, []);
    const copyToClipboard = async (text, key) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedStates(prev => ({ ...prev, [key]: true }));
            setTimeout(() => {
                setCopiedStates(prev => ({ ...prev, [key]: false }));
            }, 2000);
        }
        catch (err) {
            console.error('Failed to copy text: ', err);
            setError('Failed to copy to clipboard.');
        }
    };
    return (React.createElement(Container, { fluid: true },
        React.createElement(Header, { as: "h1", textAlign: "center" },
            "DocFluidForm Web Component",
            React.createElement(Header.Subheader, null, "Universal form component that works everywhere")),
        React.createElement(ExampleSection, null,
            React.createElement(Header, { as: "h2" }, "Live Demo"),
            React.createElement(ComponentDemo, null,
                React.createElement("doc-fluid-form", null))),
        React.createElement(ExampleSection, null,
            error && React.createElement(InComponentErrorRenderer, { error: error }),
            React.createElement(Header, { as: "h2" }, "Integration Examples"),
            React.createElement(Tab, { panes: [
                    {
                        menuItem: {
                            key: 'html',
                            icon: 'html5',
                            content: 'HTML/Vanilla JS'
                        },
                        render: () => (React.createElement(Tab.Pane, null,
                            React.createElement(CodeContainer, null,
                                React.createElement(Button, { className: "copy-button", icon: copiedStates['html'] ? 'check' : 'copy', color: copiedStates['html'] ? 'green' : 'blue', size: "small", content: copiedStates['html'] ? 'Copied!' : 'Copy', onClick: () => copyToClipboard(`<!-- Include React dependencies -->
<script src="https://unpkg.com/react@18/umd/react.production.min.js"></script>
<script src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"></script>

<!-- Use the component -->
<doc-fluid-form></doc-fluid-form>

<script>
  // Listen for form submissions
  document.addEventListener('formsubmitted', function(event) {
    console.log('Form data:', event.detail.formData);
    console.log('Form key:', event.detail.formKey);
    console.log('Timestamp:', event.detail.timestamp);
  });
</script>`, 'html') }),
                                React.createElement(CodeBlock, null, `<!-- Include React dependencies -->
<script src="https://unpkg.com/react@18/umd/react.production.min.js"></script>
<script src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"></script>

<!-- Use the component -->
<doc-fluid-form></doc-fluid-form>

<script>
  // Listen for form submissions
  document.addEventListener('formsubmitted', function(event) {
    console.log('Form data:', event.detail.formData);
    console.log('Form key:', event.detail.formKey);
    console.log('Timestamp:', event.detail.timestamp);
  });
</script>`))))
                    },
                    {
                        menuItem: {
                            key: 'react',
                            icon: 'react',
                            content: 'React'
                        },
                        render: () => (React.createElement(Tab.Pane, null,
                            React.createElement(CodeContainer, null,
                                React.createElement(Button, { className: "copy-button", icon: copiedStates['react'] ? 'check' : 'copy', color: copiedStates['react'] ? 'green' : 'blue', size: "small", content: copiedStates['react'] ? 'Copied!' : 'Copy', onClick: () => copyToClipboard(`import React, { useEffect } from 'react';

function App() {
  useEffect(() => {
    const handleFormSubmit = (e) => {
      console.log('Form submitted:', e.detail);
      // Handle the form data
      const formData = e.detail.formData;
      // Send to your API, etc.
    };

    document.addEventListener('formsubmitted', handleFormSubmit);
    return () => {
      document.removeEventListener('formsubmitted', handleFormSubmit);
    };
  }, []);

  return (
    <div>
      <h1>My App</h1>
      <doc-fluid-form></doc-fluid-form>
    </div>
  );
}`, 'react') }),
                                React.createElement(CodeBlock, null, `import React, { useEffect } from 'react';

function App() {
  useEffect(() => {
    const handleFormSubmit = (e) => {
      console.log('Form submitted:', e.detail);
      // Handle the form data
      const formData = e.detail.formData;
      // Send to your API, etc.
    };

    document.addEventListener('formsubmitted', handleFormSubmit);
    return () => {
      document.removeEventListener('formsubmitted', handleFormSubmit);
    };
  }, []);

  return (
    <div>
      <h1>My App</h1>
      <doc-fluid-form></doc-fluid-form>
    </div>
  );
}`))))
                    },
                    {
                        menuItem: {
                            key: 'vue',
                            icon: 'code',
                            content: 'Vue.js'
                        },
                        render: () => (React.createElement(Tab.Pane, null,
                            React.createElement(CodeContainer, null,
                                React.createElement(Button, { className: "copy-button", icon: copiedStates['vue'] ? 'check' : 'copy', color: copiedStates['vue'] ? 'green' : 'blue', size: "small", content: copiedStates['vue'] ? 'Copied!' : 'Copy', onClick: () => copyToClipboard(`<template>
  <div>
    <h1>My Vue App</h1>
    <doc-fluid-form @formsubmitted="handleFormSubmit"></doc-fluid-form>
  </div>
</template>

<script>
export default {
  name: 'MyVueApp',
  methods: {
    handleFormSubmit(event) {
      console.log('Form submitted:', event.detail);
      // Handle the form data
    }
  },
  mounted() {
    // Alternative event listener approach
    document.addEventListener('formsubmitted', this.handleFormSubmit);
  },
  beforeUnmount() {
    document.removeEventListener('formsubmitted', this.handleFormSubmit);
  }
}
</script>`, 'vue') }),
                                React.createElement(CodeBlock, null, `<template>
  <div>
    <h1>My Vue App</h1>
    <doc-fluid-form @formsubmitted="handleFormSubmit"></doc-fluid-form>
  </div>
</template>

<script>
export default {
  name: 'MyVueApp',
  methods: {
    handleFormSubmit(event) {
      console.log('Form submitted:', event.detail);
      // Handle the form data
    }
  },
  mounted() {
    // Alternative event listener approach
    document.addEventListener('formsubmitted', this.handleFormSubmit);
  },
  beforeUnmount() {
    document.removeEventListener('formsubmitted', this.handleFormSubmit);
  }
}
</script>`))))
                    },
                    {
                        menuItem: {
                            key: 'angular',
                            icon: 'code',
                            content: 'Angular'
                        },
                        render: () => (React.createElement(Tab.Pane, null,
                            React.createElement(CodeContainer, null,
                                React.createElement(Button, { className: "copy-button", icon: copiedStates['angular'] ? 'check' : 'copy', color: copiedStates['angular'] ? 'green' : 'blue', size: "small", content: copiedStates['angular'] ? 'Copied!' : 'Copy', onClick: () => copyToClipboard(`import { Component, OnInit, OnDestroy } from '@angular/core';

@Component({
  selector: 'app-form',
  template: \`
    <h1>My Angular App</h1>
    <doc-fluid-form></doc-fluid-form>
  \`
})
export class FormComponent implements OnInit, OnDestroy {
  private handleFormSubmit = (event: any) => {
    console.log('Form submitted:', event.detail);
    // Handle the form data
  };

  ngOnInit() {
    document.addEventListener('formsubmitted', this.handleFormSubmit);
  }

  ngOnDestroy() {
    document.removeEventListener('formsubmitted', this.handleFormSubmit);
  }
}`, 'angular') }),
                                React.createElement(CodeBlock, null, `import { Component, OnInit, OnDestroy } from '@angular/core';

@Component({
  selector: 'app-form',
  template: \`
    <h1>My Angular App</h1>
    <doc-fluid-form></doc-fluid-form>
  \`
})
export class FormComponent implements OnInit, OnDestroy {
  private handleFormSubmit = (event: any) => {
    console.log('Form submitted:', event.detail);
    // Handle the form data
  };

  ngOnInit() {
    document.addEventListener('formsubmitted', this.handleFormSubmit);
  }

  ngOnDestroy() {
    document.removeEventListener('formsubmitted', this.handleFormSubmit);
  }
}`))))
                    }
                ] })),
        formData && (React.createElement(ExampleSection, null,
            React.createElement(Header, { as: "h2" }, "Last Form Submission"),
            React.createElement(CodeContainer, null,
                React.createElement(Button, { className: "copy-button", icon: copiedStates['formdata'] ? 'check' : 'copy', color: copiedStates['formdata'] ? 'green' : 'blue', size: "small", content: copiedStates['formdata'] ? 'Copied!' : 'Copy', onClick: () => copyToClipboard(JSON.stringify(formData, null, 2), 'formdata') }),
                React.createElement(CodeBlock, null, JSON.stringify(formData, null, 2)))))));
};
export default DevDocsPage;
